/**
 * طبقة الاتصال لشاشة «التقييم والترشيح» — الحجز والترشيح
 * (design/Evaluation.dc.html · docs/03 §12).
 *
 * الراوترات في الباك إند: `app/routers/bookings.py` و`app/routers/nominations.py`.
 *
 * ⛔ القواعد اللي الملف ده بيحرسها:
 *   1) **ممنوع اختراع قيمة.** الموعد بيتعرض من `when_family_ar` اللي
 *      السيرفر حسبه بتوقيت الأسرة. لو رجع `null` بنقول «الموعد محتاج
 *      مراجعة» — **ما بنعيدش حسابه بتوقيت المتصفح**، لأن توقيت المتصفح
 *      توقيت الموظفة مش توقيت الأسرة.
 *   2) **ممنوع الفشل الصامت.** أي رد مش على الشكل المتفق عليه بيرمي خطأ
 *      صريح ومعاه المسار في السجل.
 *   3) **الأسرة بلا منطقة زمنية ما بيتحجزلهاش** — الصف بيرجع
 *      `can_book=false` ومعاه السبب، والشاشة بتقفل الزرار وتعرض السبب.
 */
import { api } from "@/api/client";

/* ══════════════ أنواع ══════════════ */

export type BookingRow = {
  id: string;
  kind: string;
  student: { id: string | null; name_ar: string };
  family: { id: string | null; name_ar: string };
  evaluator: { id: string | null; name_ar: string };
  startAt: string | null;
  durationMinutes: number | null;
  durationAr: string | null;
  familyTz: string | null;
  /** الموعد بتوقيت الأسرة — `null` معناها السيرفر ما قدرش يقرا المنطقة */
  whenFamilyAr: string | null;
  /** نفس الموعد بتوقيت الأكاديمية — الفرق بين كندا ومصر ٧ ساعات */
  whenAcademyAr: string | null;
  status: string;
  statusLabelAr: string;
  notesAr: string | null;
  version: number;
};

export type EvaluationRequest = {
  journeyId: string;
  stepNo: number;
  student: { id: string | null; name_ar: string };
  family: {
    id: string | null;
    name_ar: string;
    timezone: string | null;
    country: string | null;
  };
  canBook: boolean;
  blockedReasonAr: string | null;
};

export type Evaluator = { id: string; name_ar: string; languages: string[] };

export type NominationCandidate = {
  teacherId: string;
  nameAr: string;
  response: string;
  responseLabelAr: string;
  declineReasonAr: string | null;
};

export type NominationRow = {
  id: string;
  status: string;
  statusLabelAr: string;
  student: { id: string | null; name_ar: string };
  enrollmentId: string | null;
  subject: string | null;
  subjectLabelAr: string;
  candidates: NominationCandidate[];
  acceptedTeacherId: string | null;
  /** الحقول اللي البورتال بيبعتها ناقصة — بنقولها بالاسم مش بنملاها */
  missingContextAr: string[];
  version: number;
};

export type TeacherOption = {
  id: string;
  name_ar: string;
  subjects: string[];
  languages: string[];
};

export type EnrollmentRow = {
  id: string;
  studentId: string | null;
  familyId: string | null;
  subject: string | null;
  subjectLabelAr: string;
  labelAr: string;
  teacherId: string | null;
  status: string | null;
  /** `null` معناها **مش مسجّلة**، مش صفر */
  contractedHours: string | null;
};

/* ══════════════ حرّاس القراءة ══════════════ */

function fail(path: string, why: string): never {
  console.error(`[evaluation] ${path} — ${why}`);
  throw new Error(`${path}: ${why}`);
}

function rows(res: { data?: unknown }, path: string): Record<string, unknown>[] {
  if (!Array.isArray(res.data)) fail(path, "الرد مش قائمة");
  return res.data as Record<string, unknown>[];
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

/* ══════════════ الحجوزات ══════════════ */

function toBooking(d: Record<string, unknown>): BookingRow {
  const student = obj(d["student"]);
  const family = obj(d["family"]);
  const evaluator = obj(d["evaluator"]);
  return {
    id: str(d["id"]),
    kind: str(d["kind"]),
    student: { id: strOrNull(student["id"]), name_ar: str(student["name_ar"]) },
    family: { id: strOrNull(family["id"]), name_ar: str(family["name_ar"]) },
    evaluator: { id: strOrNull(evaluator["id"]), name_ar: str(evaluator["name_ar"]) },
    startAt: strOrNull(d["start_at"]),
    durationMinutes:
      typeof d["duration_minutes"] === "number" ? (d["duration_minutes"] as number) : null,
    durationAr: strOrNull(d["duration_ar"]),
    familyTz: strOrNull(d["family_tz"]),
    whenFamilyAr: strOrNull(d["when_family_ar"]),
    whenAcademyAr: strOrNull(d["when_academy_ar"]),
    status: str(d["status"]),
    statusLabelAr: str(d["status_label_ar"]),
    notesAr: strOrNull(d["notes_ar"]),
    version: typeof d["version"] === "number" ? (d["version"] as number) : 1,
  };
}

/** حد الصفحة — الشاشة **بتقول** إنها بتعرض أول كام لو فيه أكتر */
export const BOOKINGS_PAGE = 50;

export async function fetchBookings(params: {
  familyId?: string | null;
  studentId?: string | null;
  status?: string | null;
} = {}): Promise<{ rows: BookingRow[]; total: number }> {
  const path = "/bookings";
  const res = await api.get<unknown>(path, {
    query: {
      family_id: params.familyId ?? undefined,
      student_id: params.studentId ?? undefined,
      status: params.status ?? undefined,
      per_page: BOOKINGS_PAGE,
    },
  });
  const list = rows(res, path).map(toBooking);
  // ⛔ ممنوع الشاشة تدّعي إنها بتعرض الكل — العدد الحقيقي من `meta`
  const meta = obj((res as unknown as { meta?: unknown }).meta);
  const total = typeof meta["total"] === "number" ? (meta["total"] as number) : list.length;
  return { rows: list, total };
}

export async function fetchEvaluationRequests(): Promise<EvaluationRequest[]> {
  const path = "/evaluation/requests";
  const res = await api.get<unknown>(path);
  return rows(res, path).map((d) => {
    const student = obj(d["student"]);
    const family = obj(d["family"]);
    return {
      journeyId: str(d["journey_id"]),
      stepNo: typeof d["step_no"] === "number" ? (d["step_no"] as number) : 0,
      student: { id: strOrNull(student["id"]), name_ar: str(student["name_ar"]) },
      family: {
        id: strOrNull(family["id"]),
        name_ar: str(family["name_ar"]),
        timezone: strOrNull(family["timezone"]),
        country: strOrNull(family["country"]),
      },
      canBook: d["can_book"] === true,
      blockedReasonAr: strOrNull(d["blocked_reason_ar"]),
    };
  });
}

export async function fetchEvaluators(): Promise<Evaluator[]> {
  const path = "/evaluation/evaluators";
  const res = await api.get<unknown>(path);
  return rows(res, path).map((d) => ({
    id: str(d["id"]),
    name_ar: str(d["name_ar"]),
    languages: Array.isArray(d["languages"]) ? (d["languages"] as string[]) : [],
  }));
}

export type CreateBookingInput = {
  studentId: string;
  evaluatorUserId: string;
  /** ISO كامل — الواجهة بتحوّل اللي الموظفة كتبته لتوقيت الأسرة قبل ما تبعته */
  startAtIso: string;
  durationMinutes: number;
  journeyId?: string | null;
  stepNo?: number | null;
  notesAr?: string | null;
};

export async function createBooking(input: CreateBookingInput): Promise<{
  row: BookingRow;
  toastAr: string;
  warningsAr: string[];
}> {
  const path = "/bookings";
  const res = await api.post<unknown>(path, {
    kind: "evaluation",
    student_id: input.studentId,
    evaluator_user_id: input.evaluatorUserId,
    start_at: input.startAtIso,
    duration_minutes: input.durationMinutes,
    journey_id: input.journeyId ?? null,
    step_no: input.stepNo ?? null,
    notes_ar: input.notesAr ?? null,
  });
  const d = obj(res.data);
  if (!d["id"]) fail(path, "الحجز رجّع رد فاضي");
  const rawWarnings = (res as unknown as { warnings?: unknown }).warnings;
  const warnings = Array.isArray(rawWarnings)
    ? (rawWarnings as Record<string, unknown>[]).map((w) => str(w["message_ar"]))
    : [];
  return { row: toBooking(d), toastAr: str(d["toast_ar"]), warningsAr: warnings };
}

export async function cancelBooking(id: string, reasonAr: string): Promise<string> {
  const res = await api.post<unknown>(`/bookings/${id}/cancel`, { reason_ar: reasonAr });
  return str(obj(res.data)["toast_ar"]) || "الحجز اتلغى";
}

/* ══════════════ الترشيح ══════════════ */

function toNomination(d: Record<string, unknown>): NominationRow {
  const student = obj(d["student"]);
  const context = obj(d["context"]);
  const cands = Array.isArray(d["candidates"])
    ? (d["candidates"] as Record<string, unknown>[])
    : [];
  return {
    id: str(d["id"]),
    status: str(d["status"]),
    statusLabelAr: str(d["status_label_ar"]),
    student: { id: strOrNull(student["id"]), name_ar: str(student["name_ar"]) },
    enrollmentId: strOrNull(d["enrollment_id"]),
    subject: strOrNull(d["subject"]),
    subjectLabelAr: str(d["subject_label_ar"]),
    candidates: cands.map((c) => ({
      teacherId: str(c["teacher_id"]),
      nameAr: str(c["name_ar"]),
      response: str(c["response"]),
      responseLabelAr: str(c["response_label_ar"]),
      declineReasonAr: strOrNull(c["decline_reason_ar"]),
    })),
    acceptedTeacherId: strOrNull(d["accepted_teacher_id"]),
    missingContextAr: Array.isArray(context["missing_ar"])
      ? (context["missing_ar"] as string[])
      : [],
    version: typeof d["version"] === "number" ? (d["version"] as number) : 1,
  };
}

export async function fetchNominations(status?: string | null): Promise<NominationRow[]> {
  const path = "/nominations";
  const res = await api.get<unknown>(path, {
    query: { status: status ?? undefined, per_page: 50 },
  });
  return rows(res, path).map(toNomination);
}

export async function fetchTeacherCandidates(
  subject?: string | null,
): Promise<TeacherOption[]> {
  const path = "/nominations/candidates";
  const res = await api.get<unknown>(path, { query: { subject: subject ?? undefined } });
  return rows(res, path).map((d) => ({
    id: str(d["id"]),
    name_ar: str(d["name_ar"]),
    subjects: Array.isArray(d["subjects"]) ? (d["subjects"] as string[]) : [],
    languages: Array.isArray(d["languages"]) ? (d["languages"] as string[]) : [],
  }));
}

export async function createNomination(input: {
  studentId: string;
  subject: string;
  teacherIds: string[];
  noteAr?: string | null;
}): Promise<{ row: NominationRow; toastAr: string }> {
  const path = "/nominations";
  const res = await api.post<unknown>(path, {
    student_id: input.studentId,
    subject: input.subject,
    teacher_ids: input.teacherIds,
    note_ar: input.noteAr ?? null,
  });
  const d = obj(res.data);
  if (!d["id"]) fail(path, "الترشيح رجّع رد فاضي");
  return { row: toNomination(d), toastAr: str(d["toast_ar"]) };
}

export async function respondToNomination(
  id: string,
  input: { teacherId: string; response: "accepted" | "declined"; reasonAr?: string | null },
): Promise<{ row: NominationRow; toastAr: string }> {
  const path = `/nominations/${id}/respond`;
  const res = await api.post<unknown>(path, {
    teacher_id: input.teacherId,
    response: input.response,
    reason_ar: input.reasonAr ?? null,
  });
  const d = obj(res.data);
  if (!d["id"]) fail(path, "تسجيل الرد رجّع رد فاضي");
  return { row: toNomination(d), toastAr: str(d["toast_ar"]) };
}

/* ══════════════ الاشتراكات ══════════════ */

export async function fetchEnrollments(params: {
  familyId?: string | null;
  studentId?: string | null;
}): Promise<EnrollmentRow[]> {
  const path = "/enrollments";
  const res = await api.get<unknown>(path, {
    query: {
      family_id: params.familyId ?? undefined,
      student_id: params.studentId ?? undefined,
    },
  });
  return rows(res, path).map((d) => ({
    id: str(d["id"]),
    studentId: strOrNull(d["student_id"]),
    familyId: strOrNull(d["family_id"]),
    subject: strOrNull(d["subject"]),
    subjectLabelAr: str(d["subject_label_ar"]),
    labelAr: str(d["label_ar"]),
    teacherId: strOrNull(d["teacher_id"]),
    status: strOrNull(d["status"]),
    contractedHours: strOrNull(d["contracted_hours"]),
  }));
}

export async function activateEnrollment(
  id: string,
  input: { portalCourseId?: string | null; noteAr?: string | null } = {},
): Promise<string> {
  const res = await api.post<unknown>(`/enrollments/${id}/activate`, {
    portal_course_id: input.portalCourseId ?? null,
    note_ar: input.noteAr ?? null,
  });
  return str(obj(res.data)["toast_ar"]) || "الكورس اتفعّل";
}

/* ══════════════ المواد ══════════════ */

/** قايمة المواد — نفس `models/common.py::Subject` بالحرف */
export const SUBJECTS: { key: string; labelAr: string }[] = [
  { key: "quran", labelAr: "قرآن" },
  { key: "noor_bayan", labelAr: "نور البيان" },
  { key: "tajweed", labelAr: "تجويد" },
  { key: "arabic_non_native", labelAr: "عربي لغير الناطقين" },
  { key: "islamic_studies", labelAr: "دراسات إسلامية" },
  { key: "english", labelAr: "إنجليزي" },
  { key: "unspecified", labelAr: "مادة مش محددة" },
];

/* ══════════════ نتيجة حصة التقييم (خطوة ٥) ══════════════
   🔴 الوقفة اللي القسم ده بيفكّها: خطوة «حصة التقييم» فان-أوت **لكل طالب**،
      وماكانش فيه أي مسار في الواجهة بيقفل بند طالب — فالمقيّمة كانت بتاخد
      «لسه فيه بنود ما خلصوش» بلا أي طريقة تعملها.

   ⛔ الشاشة **ما بتحسبش** المتوسط ولا بتحوّل الموعد — الاتنين بيجوا محسوبين
      من السيرفر (`average` نص · `whenFamilyAr` بتوقيت الأسرة). */

export type EvalChecklistItem = {
  key: string;
  labelAr: string;
  done: boolean;
  required: boolean;
};

export type EvalSessionTask = {
  id: string;
  state: string;
  version: number;
  /** الاستلام بيثبّت المسؤولية — القايمة والإقفال مقفولين قبله */
  claimedByMe: boolean;
  checklist: EvalChecklistItem[];
  missingLabelsAr: string[];
};

export type EvalSessionStudent = {
  id: string | null;
  name_ar: string;
  done: boolean;
  /** الموعد بتوقيت الأسرة زي ما السيرفر حسبه — `null` = محتاج مراجعة */
  whenFamilyAr: string | null;
  whenAcademyAr: string | null;
  bookingStatusLabelAr: string | null;
  evaluationId: string | null;
  /** نص زي `"3.20"` — **مش** رقم، عشان ما يتقربش في المتصفح */
  average: string | null;
};

export type EvaluationSession = {
  journeyId: string;
  stepNo: number;
  stepTitleAr: string;
  family: { id: string | null; name_ar: string; timezone: string | null };
  task: EvalSessionTask;
  students: EvalSessionStudent[];
  remaining: number;
  total: number;
  canClose: boolean;
  blockedReasonAr: string | null;
};

export type RubricItem = { key: string; labelAr: string; min: number; max: number };
export type LabeledKey = { key: string; labelAr: string };
export type RubricForm = {
  items: RubricItem[];
  version: number;
  confidence: LabeledKey[];
  styles: LabeledKey[];
};

function num(v: unknown, fallback: number): number {
  return typeof v === "number" ? v : fallback;
}

function strList(v: unknown): string[] {
  return Array.isArray(v) ? (v as unknown[]).filter((x) => typeof x === "string") as string[] : [];
}

function toSession(d: Record<string, unknown>): EvaluationSession {
  const family = obj(d["family"]);
  const task = obj(d["task"]);
  const students = Array.isArray(d["students"])
    ? (d["students"] as Record<string, unknown>[])
    : [];
  return {
    journeyId: str(d["journey_id"]),
    stepNo: num(d["step_no"], 0),
    stepTitleAr: str(d["step_title_ar"]),
    family: {
      id: strOrNull(family["id"]),
      name_ar: str(family["name_ar"]),
      timezone: strOrNull(family["timezone"]),
    },
    task: {
      id: str(task["id"]),
      state: str(task["state"]),
      version: num(task["version"], 1),
      claimedByMe: task["claimed_by_me"] === true,
      checklist: (Array.isArray(task["checklist"])
        ? (task["checklist"] as Record<string, unknown>[])
        : []
      ).map((c) => ({
        key: str(c["key"]),
        labelAr: str(c["label_ar"]),
        done: c["done"] === true,
        required: c["required"] !== false,
      })),
      missingLabelsAr: strList(task["missing_labels_ar"]),
    },
    students: students.map((s) => {
      const booking = obj(s["booking"]);
      return {
        id: strOrNull(s["id"]),
        name_ar: str(s["name_ar"]),
        done: s["done"] === true,
        whenFamilyAr: strOrNull(booking["when_family_ar"]),
        whenAcademyAr: strOrNull(booking["when_academy_ar"]),
        bookingStatusLabelAr: strOrNull(booking["status_label_ar"]),
        evaluationId: strOrNull(s["evaluation_id"]),
        average: strOrNull(s["average"]),
      };
    }),
    remaining: num(d["remaining"], 0),
    total: num(d["total"], 0),
    canClose: d["can_close"] === true,
    blockedReasonAr: strOrNull(d["blocked_reason_ar"]),
  };
}

export async function fetchEvaluationSessions(): Promise<EvaluationSession[]> {
  const path = "/evaluation/sessions";
  const res = await api.get<unknown>(path);
  return rows(res, path).map(toSession);
}

/** بنود الروبرك **من السيرفر** — البنود ونسختها بيتغيّروا، والواجهة ما بتخزّنهمش */
export async function fetchRubric(): Promise<RubricForm> {
  const path = "/rubrics/student/current";
  const res = await api.get<unknown>(path);
  const meta = obj((res as unknown as { meta?: unknown }).meta);
  const labeled = (v: unknown): LabeledKey[] =>
    (Array.isArray(v) ? (v as Record<string, unknown>[]) : []).map((x) => ({
      key: str(x["key"]),
      labelAr: str(x["label_ar"]),
    }));
  const items = rows(res, path).map((d) => ({
    key: str(d["key"]),
    labelAr: str(d["label_ar"]),
    min: num(d["min"], 1),
    max: num(d["max"], 5),
  }));
  if (items.length === 0) fail(path, "الروبرك رجع بلا بنود");
  return {
    items,
    version: num(meta["rubric_version"], 1),
    confidence: labeled(meta["confidence"]),
    styles: labeled(meta["teacher_styles"]),
  };
}

export type SubmitEvaluationInput = {
  studentId: string;
  journeyId: string;
  stepNo: number;
  /** درجة لكل بند — الواجهة بتبعتها زي ما هي، **والمتوسط بيتحسب في السيرفر** */
  rubric: Record<string, number>;
  notesAr?: string | null;
  recommendedSubjects?: string[];
  evaluatorConfidence?: string | null;
  preferredTeacherStyle?: string | null;
  /** الاستمارة الموسّعة — كلها اختيارية.
   *
   * ⚠️ **اختيارية عن قصد:** الحقل الإجباري كان هيوقف المقيّمة عن تسجيل
   *    نتيجة حصة حصلت فعلًا لمجرد إنها ما عرفتش هوايات الطالب. الروبرك
   *    هو الإجباري الوحيد. */
  studentLevel?: string | null;
  profile?: Partial<Record<ProfileField, string>>;
};

/** خانات الاستمارة الوصفية — ⛔ الأسماء لازم تطابق الخادم بالحرف. */
export const PROFILE_FIELDS = [
  { key: "hobbies_ar", label: "هواياته واهتماماته" },
  { key: "likes_ar", label: "الحاجات اللي بيحبها" },
  { key: "current_study_level_ar", label: "مستواه الدراسي الحالي" },
  { key: "prior_study_ar", label: "درس إيه قبل كده في المادة" },
  { key: "prior_study_place_ar", label: "درس فين قبل كده" },
  { key: "strengths_ar", label: "نقاط القوة" },
  { key: "weaknesses_ar", label: "محتاج يتطوّر في إيه" },
  { key: "recommendations_ar", label: "توصياتك للمرحلة الجاية" },
] as const;

export type ProfileField = (typeof PROFILE_FIELDS)[number]["key"];

export async function submitEvaluation(input: SubmitEvaluationInput): Promise<{
  toastAr: string;
  warningsAr: string[];
  remainingChildren: number | null;
}> {
  const path = "/evaluations";
  const res = await api.post<unknown>(path, {
    student_id: input.studentId,
    journey_id: input.journeyId,
    step_no: input.stepNo,
    rubric: input.rubric,
    notes_ar: input.notesAr ?? null,
    recommended_subjects: input.recommendedSubjects ?? [],
    evaluator_confidence: input.evaluatorConfidence ?? null,
    preferred_teacher_style: input.preferredTeacherStyle ?? null,
    ...(input.studentLevel ? { student_level: input.studentLevel } : {}),
    // الخانة الفاضية ما بتتبعتش خالص — `null` معناها «ما اتسجّلتش»،
    // ونص فاضي كان هيتقرا «المقيّمة كتبت وسابته فاضي».
    ...Object.fromEntries(
      Object.entries(input.profile ?? {})
        .filter(([, v]) => typeof v === "string" && v.trim().length > 0)
        .map(([k, v]) => [k, (v as string).trim()]),
    ),
  });
  const d = obj(res.data);
  if (!d["id"]) fail(path, "تسجيل النتيجة رجّع رد فاضي");
  const rawWarnings = (res as unknown as { warnings?: unknown }).warnings;
  const warnings = Array.isArray(rawWarnings)
    ? (rawWarnings as Record<string, unknown>[]).map((w) => str(w["message_ar"]))
    : [];
  return {
    toastAr: str(d["toast_ar"]),
    warningsAr: warnings,
    remainingChildren:
      typeof d["remaining_children"] === "number" ? (d["remaining_children"] as number) : null,
  };
}
